import { Controller } from "stimulus";


export default class extends Controller {
  static values = {
    url: String,
    serviceId: Number
  }

  connect() {
    this.startAvailableDates = [];
    this.rawAvailableDates = [];
    this.loadedMonths = new Set();
    this.availableDateTimes = {};
    this.initializeStartDatePicker();
    this.initializeEndDatePicker();
    this.setupPetCountListener();
    this.setupTimeChangeListeners();
    this.updatePetCountDependentState();
    this.updateSubmitButtonState();
  }

  parseWindowDate(value) {
    if (!value) return null;

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  getBlockAvailabilityBefore() {
    return this.parseWindowDate(window.block_availability_before);
  }

  getBlockAvailabilityAfter() {
    return this.parseWindowDate(window.block_availability_after);
  }

  isDateBlocked(date) {
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);

    const blockBefore = this.getBlockAvailabilityBefore();
    if (blockBefore) {
      const blockBeforeDate = new Date(blockBefore);
      blockBeforeDate.setHours(0, 0, 0, 0);
      if (dateToCheck < blockBeforeDate) return true;
    }

    const blockAfter = this.getBlockAvailabilityAfter();
    if (blockAfter) {
      const blockAfterDate = new Date(blockAfter);
      blockAfterDate.setHours(0, 0, 0, 0);
      if (dateToCheck > blockAfterDate) return true;
    }

    return false;
  }

  parseTimeString(timeStr) {
    // Parse time strings like "8:00 AM", "12:30 PM"
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();

    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    return { hours, minutes };
  }

  formatDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  isTimeBlockedOnDate(date, timeStr) {
    const blockBefore = this.getBlockAvailabilityBefore();
    if (!blockBefore) return false;

    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    const blockDate = new Date(blockBefore);
    const blockDateMidnight = new Date(blockBefore);
    blockDateMidnight.setHours(0, 0, 0, 0);

    // Only filter times if it's the same day
    if (dateToCheck.getTime() !== blockDateMidnight.getTime()) return false;

    const parsed = this.parseTimeString(timeStr);
    if (!parsed) return false;

    const timeDate = new Date(date);
    timeDate.setHours(parsed.hours, parsed.minutes, 0, 0);

    return timeDate < blockBefore;
  }

  hasAvailableTimesOnDate(date) {
    if (this.isDateBlocked(date)) return false;

    const dateKey = this.formatDateKey(date);
    const dateTimes = this.availableDateTimes[dateKey];
    if (!dateTimes) return false;

    const allTimes = dateTimes.dropoff_times || [];
    const availableTimes = allTimes.filter(time => !this.isTimeBlockedOnDate(date, time));
    return availableTimes.length > 0;
  }

  getConsecutiveEndDates(startDate) {
    const rawDateMap = new Map();
    this.rawAvailableDates.forEach(dateStr => {
      rawDateMap.set(new Date(dateStr).toDateString(), dateStr);
    });

    const endDates = [];
    const current = new Date(startDate);
    current.setDate(current.getDate() + 1);

    for (let i = 0; i < 365; i++) {
      const monthKey = current.getFullYear() + '-' + (current.getMonth() + 1);
      if (!this.loadedMonths.has(monthKey)) break;

      const dateKey = this.formatDateKey(current);
      const dateStr = current.toDateString();
      const isInRawDates = rawDateMap.has(dateStr);

      if (!isInRawDates) {
        break;
      }

      const dateTimes = this.availableDateTimes[dateKey];
      if (dateTimes && dateTimes.pickup_times && dateTimes.pickup_times.length > 0) {
        endDates.push(rawDateMap.get(dateStr));
      }

      current.setDate(current.getDate() + 1);
    }

    return endDates;
  }

  // In popup mode there's no .flatpickr-wrapper, so visibility is toggled on the
  // input itself; closest() keeps this working if a wrapper is reintroduced.
  pickerWrapper(calendar) {
    const input = calendar && calendar.input;
    if (!input) return null;
    return input.closest('.flatpickr-wrapper') || input;
  }

  // The calendar floats over the page (popup mode, appended to <body>) and opens
  // above or below the input automatically — same as the client-portal pickers.
  // window.innerHeight extends behind the native app's bottom tab bar, so a month
  // that opens below a mid-page input can land partly behind it; nudge the page the
  // minimum amount to bring the whole calendar into the safe area between the app
  // header and the tab bar. flatpickr only repositions on resize (not scroll), so
  // the popup rides along with the input as we scroll.
  nudgePickerIntoView(instance) {
    if (window.test) return;
    const calendar = instance && instance.calendarContainer;
    if (!calendar) return;
    const bottomInset = 96; // native tab bar overlay
    const topInset = 64;    // app header overlay
    const gap = 8;
    requestAnimationFrame(() => {
      const rect = calendar.getBoundingClientRect();
      const safeBottom = window.innerHeight - bottomInset - gap;
      const safeTop = topInset + gap;
      let delta = 0;
      if (rect.bottom > safeBottom) {
        delta = rect.bottom - safeBottom;
      } else if (rect.top < safeTop) {
        delta = rect.top - safeTop;
      }
      if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: 'smooth' });
    });
  }

  showEndDatePicker() {
    const wrapper = this.pickerWrapper(this.endCalendar);
    if (wrapper) wrapper.classList.remove('d-none');
    const disabledEndAt = document.getElementById('js-boarding-end-at-disabled');
    if (disabledEndAt) {
      disabledEndAt.classList.add('d-none');
    }
  }

  hideEndDatePicker() {
    const wrapper = this.pickerWrapper(this.endCalendar);
    if (wrapper) wrapper.classList.add('d-none');
    const disabledEndAt = document.getElementById('js-boarding-end-at-disabled');
    if (disabledEndAt) {
      disabledEndAt.classList.remove('d-none');
    }
    this.resetCheckOutTime();
  }

  resetCheckInTime() {
    const select = document.getElementById('js-boarding-check-in-time');
    if (select) {
      select.innerHTML = '<option value="">Select Time</option>';
      select.disabled = true;
      $('.selectpicker').selectpicker('refresh');
    }
    this.updateSubmitButtonState();
  }

  resetCheckOutTime() {
    const select = document.getElementById('js-boarding-check-out-time');
    if (select) {
      select.innerHTML = '<option value="">Select Time</option>';
      select.disabled = true;
      $('.selectpicker').selectpicker('refresh');
    }
    this.updateSubmitButtonState();
  }

  setupPetCountListener() {
    const petInput = document.getElementById('js-number-of-pets');
    if (petInput) {
      petInput.addEventListener('change', () => this.handlePetCountChange());
      petInput.addEventListener('keyup', () => this.handlePetCountChange());
      petInput.addEventListener('input', () => this.updatePetCountDependentState());
    }
  }

  petCountIsValid() {
    const input = document.getElementById('js-number-of-pets');
    if (!input) return true;
    const value = parseInt(input.value, 10);
    const min = parseInt(input.min, 10) || 1;
    return !isNaN(value) && value >= min;
  }

  updatePetCountDependentState() {
    const section = document.getElementById('js-boarding-datetime-section');
    if (section) {
      section.classList.toggle('is-disabled', !this.petCountIsValid());
    }
    this.updateSubmitButtonState();
  }

  setupTimeChangeListeners() {
    var controller = this;
    var checkInTime = document.getElementById('js-boarding-check-in-time');
    var checkOutTime = document.getElementById('js-boarding-check-out-time');

    if (checkInTime) {
      checkInTime.addEventListener('change', function() {
        controller.updateSubmitButtonState();
      });
    }
    if (checkOutTime) {
      checkOutTime.addEventListener('change', function() {
        controller.updateSubmitButtonState();
      });
    }
  }

  updateSubmitButtonState() {
    var submitButton = document.getElementById('booking-btn');
    if (!submitButton) return;

    var startAt = document.getElementById('js-boarding-start-at');
    var checkInTime = document.getElementById('js-boarding-check-in-time');
    var endAt = document.getElementById('js-boarding-end-at');
    var checkOutTime = document.getElementById('js-boarding-check-out-time');

    var startAtValue = startAt ? startAt.value : '';
    var checkInTimeValue = checkInTime ? checkInTime.value : '';
    var endAtValue = endAt ? endAt.value : '';
    var checkOutTimeValue = checkOutTime ? checkOutTime.value : '';

    if (this.petCountIsValid() && startAtValue && checkInTimeValue && endAtValue && checkOutTimeValue) {
      submitButton.disabled = false;
    } else {
      submitButton.disabled = true;
    }
  }

  handlePetCountChange() {
    this.updatePetCountDependentState();
    if (!this.petCountIsValid()) return;

    const selectedStartDate = this.startCalendar.selectedDates[0];
    const selectedEndDate = this.endCalendar.selectedDates[0];

    this.loadedMonths = new Set();
    this.preloadAvailableDates(this.startCalendar, () => {
      // After fetch, check if selected dates are still valid
      if (selectedStartDate && !this.startAvailableDates.some(d => new Date(d).getTime() === selectedStartDate.getTime())) {
        this.startCalendar.clear();
        this.endCalendar.clear();
        this.endCalendar.set('clickOpens', false);
        this.hideEndDatePicker();
        this.resetCheckInTime();
      } else if (selectedStartDate) {
        // Start date still valid, check end date
        const filteredDates = this.getConsecutiveEndDates(selectedStartDate);
        this.endCalendar.set('enable', filteredDates);

        if (selectedEndDate && !filteredDates.some(d => new Date(d).getTime() === selectedEndDate.getTime())) {
          this.endCalendar.clear();
          this.hideEndDatePicker();
        }
      }
    });
  }

  get locationId() {
    return document.getElementById('selected_location_id')?.value;
  }

  get petCount() {
    return document.getElementById('js-number-of-pets')?.value || 1;
  }

  initializeStartDatePicker() {
    const controller = this;
    const startInput = document.getElementById('js-boarding-start-at');

    this.startCalendar = flatpickr(startInput, {
      dateFormat: "D d M Y",
      allowInput: false,
      enable: [],
      disableMobile: true,
      ...(window.test ? {} : { minDate: "today" }),
      onReady(dates, dateStr, instance) {
        controller.preloadAvailableDates(instance);
      },
      onOpen(dates, dateStr, instance) {
        controller.nudgePickerIntoView(instance);
      },
      onMonthChange(dates, dateStr, instance) {
        controller.fetchAvailableDates(instance);
      },
      onChange(dates) {
        if (dates.length > 0) {
          controller.handleStartDateSelected(dates[0]);
        }
        controller.updateSubmitButtonState();
      }
    });
  }

  initializeEndDatePicker() {
    const controller = this;
    const endInput = document.getElementById('js-boarding-end-at');

    this.endCalendar = flatpickr(endInput, {
      dateFormat: "D d M Y",
      allowInput: false,
      clickOpens: false,
      enable: [],
      disableMobile: true,
      ...(window.test ? {} : { minDate: "today" }),
      onOpen(dates, dateStr, instance) {
        controller.nudgePickerIntoView(instance);
      },
      onMonthChange(dates, dateStr, instance) {
        controller.fetchEndDateAvailableDates(instance);
      },
      onYearChange(dates, dateStr, instance) {
        controller.fetchEndDateAvailableDates(instance);
      },
      onChange(dates) {
        if (dates.length > 0) {
          controller.handleEndDateSelected(dates[0]);
        }
        controller.updateSubmitButtonState();
      }
    });

    // The end input ships with `d-none` in the markup; normalize that here and let
    // hideEndDatePicker() own its visibility until a start date is picked.
    endInput.classList.remove('d-none');
    // Hide until start date is selected
    this.hideEndDatePicker();
  }

  handleEndDateSelected(endDate) {
    this.updateCheckOutTimeOptions(endDate);
    this.updateNightSummary();
  }

  updateNightSummary() {
    const summary = document.getElementById('js-night-summary');
    const nightCount = document.getElementById('js-boarding-night-count');
    if (!summary || !nightCount) return;

    const startDate = this.startCalendar.selectedDates[0];
    const endDate = this.endCalendar.selectedDates[0];

    if (!startDate || !endDate) {
      summary.classList.add('d-none');
      return;
    }

    // Calculate nights (difference in days)
    const msPerDay = 24 * 60 * 60 * 1000;
    const nights = Math.round((endDate - startDate) / msPerDay);

    nightCount.textContent = nights === 1 ? '1 night' : `${nights} nights`;
    summary.classList.remove('d-none');
  }

  handleStartDateSelected(startDate) {
    // Check for fixed duration mode
    if (window.fixed_duration && window.fixed_duration_days) {
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + window.fixed_duration_days);
      this.endCalendar.set('enable', [endDate]);
      this.endCalendar.setDate(endDate);

      // Update the disabled display field with formatted date
      const endAtDisabled = document.getElementById('js-boarding-end-at-disabled');
      if (endAtDisabled) {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const formatted = `${days[endDate.getDay()]} ${endDate.getDate()} ${months[endDate.getMonth()]} ${endDate.getFullYear()}`;
        endAtDisabled.value = formatted;
      }

      // Show night summary
      const nightSummary = document.getElementById('js-night-summary');
      if (nightSummary) {
        nightSummary.classList.remove('d-none');
      }

      this.updateCheckInTimeOptions(startDate);
      this.updateCheckOutTimeOptions(endDate);
      this.updateNightSummary();
      this.updateSubmitButtonState();
      return;
    }

    // Show end date picker and hide disabled placeholder
    this.endCalendar.set('clickOpens', true);
    this.showEndDatePicker();

    // Set min date to day after start
    const minEnd = new Date(startDate);
    minEnd.setDate(minEnd.getDate() + 1);
    this.endCalendar.set('minDate', minEnd);

    // Only allow consecutive available dates (weekday-only gaps don't break the chain)
    const filteredDates = this.getConsecutiveEndDates(startDate);
    this.endCalendar.set('enable', filteredDates);

    // Clear any previous end date selection
    this.endCalendar.clear();
    this.resetCheckOutTime();

    // Jump to the first available date's month
    if (filteredDates.length > 0) {
      this.endCalendar.jumpToDate(filteredDates[0]);
    }

    // Update check-in time options based on selected day
    this.updateCheckInTimeOptions(startDate);

    // Hide night summary since end date was cleared
    this.updateNightSummary();
  }

  updateCheckInTimeOptions(date) {
    const select = document.getElementById('js-boarding-check-in-time');
    if (!select) return;

    const dateKey = this.formatDateKey(date);
    const dateTimes = this.availableDateTimes[dateKey];
    const allTimes = (dateTimes && dateTimes.dropoff_times) || [];

    // Filter out times that are before block_availability_before on the same day
    const availableTimes = allTimes.filter(time => !this.isTimeBlockedOnDate(date, time));

    if (availableTimes.length === 0) {
      select.disabled = true;
      select.innerHTML = '<option value="">Select Time</option>';
      $('.selectpicker').selectpicker('refresh');
      return;
    }

    // Populate options without placeholder
    select.innerHTML = '';
    availableTimes.forEach(time => {
      const option = document.createElement('option');
      option.value = time;
      option.textContent = time;
      select.appendChild(option);
    });

    select.disabled = false;
    $('.selectpicker').selectpicker('refresh');
    // Auto-select first option for fixed duration
    if (window.fixed_duration && availableTimes.length > 0) {
      $(select).selectpicker('val', availableTimes[0]);
    }
  }

  updateCheckOutTimeOptions(date) {
    const select = document.getElementById('js-boarding-check-out-time');
    if (!select) return;

    const dateKey = this.formatDateKey(date);
    const dateTimes = this.availableDateTimes[dateKey];
    const availableTimes = (dateTimes && dateTimes.pickup_times) || [];

    if (availableTimes.length === 0) {
      select.disabled = true;
      select.innerHTML = '<option value="">Select Time</option>';
      $('.selectpicker').selectpicker('refresh');
      return;
    }

    // Populate options without placeholder
    select.innerHTML = '';
    availableTimes.forEach(time => {
      const option = document.createElement('option');
      option.value = time;
      option.textContent = time;
      select.appendChild(option);
    });

    select.disabled = false;
    $('.selectpicker').selectpicker('refresh');
    // Auto-select first option for fixed duration
    if (window.fixed_duration && availableTimes.length > 0) {
      $(select).selectpicker('val', availableTimes[0]);
    }
  }

  fetchEndDateAvailableDates(calendar) {
    const startDate = this.startCalendar.selectedDates[0];
    if (!startDate) return;

    const monthKey = calendar.currentYear + '-' + (calendar.currentMonth + 1);
    if (this.loadedMonths.has(monthKey)) return;

    // Skip months that are entirely past block_availability_after
    const blockAfter = this.getBlockAvailabilityAfter();
    if (blockAfter) {
      const monthStart = new Date(calendar.currentYear, calendar.currentMonth, 1);
      if (monthStart > blockAfter) return;
    }

    this.loadedMonths.add(monthKey);

    const date = '01-' + (calendar.currentMonth + 1) + '-' + calendar.currentYear;
    const controller = this;

    $.ajax({
      url: this.urlValue,
      type: "GET",
      dataType: "json",
      data: {
        service_id: this.serviceIdValue,
        location_id: this.locationId,
        pets: this.petCount,
        start_date: date
      },
      success(response) {
        const availableDates = response.available_dates || [];
        Object.assign(controller.availableDateTimes, response.available_dates_with_times || {});
        controller.rawAvailableDates = [...new Set([...controller.rawAvailableDates, ...availableDates])];
        const filteredDates = controller.getConsecutiveEndDates(startDate);
        calendar.set('enable', filteredDates);
      }
    });
  }

  preloadAvailableDates(calendar, callback) {
    const controller = this;
    const now = new Date();
    const monthsToFetch = [];
    const blockAfter = this.getBlockAvailabilityAfter();

    for (let i = 0; i < 3; i++) {
      const month = (now.getMonth() + i) % 12;
      const year = now.getFullYear() + Math.floor((now.getMonth() + i) / 12);

      // Skip months that are entirely past block_availability_after
      if (blockAfter) {
        const monthStart = new Date(year, month, 1);
        if (monthStart > blockAfter) continue;
      }

      const key = year + '-' + (month + 1);
      monthsToFetch.push({ date: '01-' + (month + 1) + '-' + year, key: key });
      this.loadedMonths.add(key);
    }

    let completed = 0;
    let allDates = [];

    monthsToFetch.forEach(({ date }) => {
      $.ajax({
        url: this.urlValue,
        type: "GET",
        dataType: "json",
        data: {
          service_id: this.serviceIdValue,
          location_id: this.locationId,
          pets: this.petCount,
          start_date: date
        },
        success(response) {
          const availableDates = response.available_dates || [];
          Object.assign(controller.availableDateTimes, response.available_dates_with_times || {});
          allDates = allDates.concat(availableDates);
          completed++;

          if (completed === monthsToFetch.length) {
            allDates = [...new Set(allDates)];
            controller.rawAvailableDates = allDates;

            const filteredDates = allDates.filter(dateStr => {
              const d = new Date(dateStr);
              if (window.fixed_duration) {
                return controller.hasAvailableTimesOnDate(d);
              }
              return controller.hasAvailableTimesOnDate(d) &&
                controller.getConsecutiveEndDates(d).length > 0;
            });
            controller.startAvailableDates = filteredDates;
            calendar.set('enable', filteredDates);

            controller.updateAvailabilityUI(filteredDates);

            if (callback) callback();
          }
        }
      });
    });
  }

  fetchAvailableDates(calendar, callback) {
    const monthKey = calendar.currentYear + '-' + (calendar.currentMonth + 1);

    if (this.loadedMonths.has(monthKey)) {
      if (callback) callback();
      return;
    }

    // Skip months that are entirely past block_availability_after
    const blockAfter = this.getBlockAvailabilityAfter();
    if (blockAfter) {
      const monthStart = new Date(calendar.currentYear, calendar.currentMonth, 1);
      if (monthStart > blockAfter) {
        if (callback) callback();
        return;
      }
    }

    this.loadedMonths.add(monthKey);

    const date = '01-' + (calendar.currentMonth + 1) + '-' + calendar.currentYear;
    const controller = this;

    $.ajax({
      url: this.urlValue,
      type: "GET",
      dataType: "json",
      data: {
        service_id: this.serviceIdValue,
        location_id: this.locationId,
        pets: this.petCount,
        start_date: date
      },
      success(response) {
        const availableDates = response.available_dates || [];
        Object.assign(controller.availableDateTimes, response.available_dates_with_times || {});
        controller.rawAvailableDates = [...new Set([...controller.rawAvailableDates, ...availableDates])];

        const filteredDates = controller.rawAvailableDates.filter(dateStr => {
          const d = new Date(dateStr);
          if (window.fixed_duration) {
            return controller.hasAvailableTimesOnDate(d);
          }
          return controller.hasAvailableTimesOnDate(d) &&
            controller.getConsecutiveEndDates(d).length > 0;
        });
        controller.startAvailableDates = filteredDates;
        calendar.set('enable', filteredDates);

        controller.updateAvailabilityUI(filteredDates);

        if (callback) callback();
      }
    });
  }

  updateAvailabilityUI(filteredDates) {
    const errorMessage = document.getElementById('js-error-no-space');
    const startAtDisabled = document.getElementById('js-boarding-start-at-disabled');
    const endAtDisabled = document.getElementById('js-boarding-end-at-disabled');
    const nightSummary = document.getElementById('js-night-summary');
    const startWrapper = this.pickerWrapper(this.startCalendar);

    if (filteredDates.length === 0) {
      if (errorMessage) errorMessage.classList.remove('d-none');
      if (startAtDisabled) startAtDisabled.classList.remove('d-none');
      if (startWrapper) startWrapper.classList.add('d-none');
      if (endAtDisabled) endAtDisabled.value = '';
      if (nightSummary) nightSummary.classList.add('d-none');
    } else {
      if (errorMessage) errorMessage.classList.add('d-none');
      if (startAtDisabled) startAtDisabled.classList.add('d-none');
      if (startWrapper) startWrapper.classList.remove('d-none');
    }
  }
}
